<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { MAX_COMMENT_LENGTH } from '@/constants';
import { DgrDialog } from '@stockmarkteam/donguri-ui';
import { Comment } from '@/types';
import { formatDateTime } from '@/utils/formatters';
import { useTeamUsers } from '@/utils/swr';

interface Props {
  comment: Comment;
}

const props = defineProps<Props>();

type Submit = { comment: Comment; content: string };
const emit = defineEmits<{
  close: [];
  submit: [Submit];
}>();

const isPostingComment = defineModel<boolean>('isPostingComment', {
  default: false,
});

const { data: users } = useTeamUsers();
const commentContent = ref(props.comment.content);
const textAreaRef = ref<HTMLTextAreaElement>();
const tooLongComment = computed(
  () => commentContent.value?.length > MAX_COMMENT_LENGTH,
);
const user = computed(() =>
  users.value?.users.find(u => u.id === props.comment.user_id),
);

const updateHeight = (event: Event) => {
  const textArea = event.target as HTMLTextAreaElement;
  textArea.style.height = '22px';
  textArea.style.height = `${Math.max(90, textArea.scrollHeight)}px`;
};

onMounted(() => {
  const es = document.getElementsByClassName('o-comment-edit-text-area');
  if (es.length === 1) {
    const event = new Event('input', {
      bubbles: true,
      cancelable: true,
    });
    es[0].dispatchEvent(event);
  }
  textAreaRef.value?.focus();
});

const canUpdateComment = () =>
  commentContent.value?.length > 0 &&
  !tooLongComment.value &&
  !isPostingComment.value;

const hideDialog = () => {
  emit('close');
  return true;
};

const updateCommentWrapper = async () => {
  if (!canUpdateComment()) return;

  emit('submit', {
    comment: props.comment,
    content: commentContent.value,
  });
};
</script>

<template>
  <DgrDialog
    title="コメント編集"
    :open="true"
    :close-interceptor-handler="hideDialog"
    @cancel="hideDialog"
    :close-on-escape="false"
  >
    <template #description>
      <div class="m-body">
        <div class="user-info" v-if="user">
          <img class="user-icon" :src="user.image_url" />
          <div>
            <div class="username c-title-bold--m">{{ user.user_name }}</div>
            <div class="c-text-regular--s">
              {{ formatDateTime(comment.created_at) }}
            </div>
          </div>
        </div>
        <div class="o-comment-edit">
          <div
            class="o-error c-formBlock__text c-formBlock__text--error"
            v-show="tooLongComment"
          >
            コメントは500文字以内で入力してください。
          </div>
          <textarea
            class="o-comment-edit-text-area c-textArea c-text-regular--m"
            ref="textAreaRef"
            :class="{
              'c-formInput--error': tooLongComment,
              'm-normal-text-area': !tooLongComment,
            }"
            v-model.trim="commentContent"
            @input="updateHeight"
            @keydown.ctrl.enter="updateCommentWrapper"
            @keydown.meta.enter="updateCommentWrapper"
          ></textarea>
        </div>
      </div>
    </template>
    <template #footer>
      <button
        class="m-cancel-button c-btn c-btn--auto"
        :class="{ disabled: isPostingComment }"
        :disabled="isPostingComment"
        @click="hideDialog"
      >
        キャンセル
      </button>
      <button
        class="m-post-button c-btn c-btn--auto c-btn--AnewsPrimary"
        :class="{ disabled: !canUpdateComment() }"
        :disabled="!canUpdateComment()"
        @click="updateCommentWrapper"
      >
        変更を保存
      </button>
    </template>
  </DgrDialog>
</template>
<style lang="scss" scoped>
.m-body {
  box-sizing: border-box;
  width: 616px;
  margin-bottom: -16px;

  .user-info {
    display: flex;
    align-items: center;
    margin-bottom: 16px;

    .user-icon {
      width: 48px;
      height: 48px;
      border-radius: 50%;
      margin-right: 8px;
    }
  }

  .o-comment-edit {
    .o-comment-edit-text-area {
      width: 100%;
      margin-bottom: 8px;
      padding: 12px 16px;
      resize: none;
      overflow: hidden;
      min-height: 22px;
      outline: none;
    }
  }

  .o-error {
    margin-top: 8px;
  }
}
</style>
